import { Server, Code2, ShieldCheck, Cloud, Headphones, GitBranch } from "lucide-react";
import { IMAGES, Reveal } from "./anim";

const SUB = [
  { icon: ShieldCheck, title: "Cybersecurity", desc: "Threat monitoring, zero-trust architecture and compliance." },
  { icon: Cloud, title: "Cloud Migration", desc: "AWS, Azure & GCP infrastructure designed to scale." },
  { icon: Headphones, title: "24/7 Helpdesk", desc: "Human support that answers before you finish typing." },
  { icon: GitBranch, title: "DevOps & CI/CD", desc: "Automated pipelines that ship code with confidence." },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-base py-24 md:py-32 px-6 md:px-10"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <div>
            <Reveal as="p" className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-5">
              [ 01 ] — What we do
            </Reveal>
            <Reveal delay={0.1} as="h2" className="font-heading font-light uppercase tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
              Two disciplines.<br />
              <span className="text-[#71717A]">One relentless standard.</span>
            </Reveal>
          </div>
          <Reveal delay={0.2} as="p" className="max-w-sm text-[#A1A1AA] md:text-right">
            We keep your systems running and build the software that moves you
            forward — under one accountable roof.
          </Reveal>
        </div>

        {/* Two dominant cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Reveal className="group relative overflow-hidden rounded-2xl border border-white/10 bg-surface min-h-[440px] flex flex-col justify-end p-8 md:p-10 hover:border-white/25 transition-colors duration-500">
            <img src={IMAGES.network} alt="Managed IT infrastructure" className="absolute inset-0 h-full w-full object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/60 to-transparent" />
            <div className="relative">
              <Server className="h-9 w-9 text-accent mb-6" />
              <div className="font-mono text-xs tracking-[0.2em] text-[#71717A] mb-3">SERVICE 01</div>
              <h3 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mb-4">
                Managed IT Services
              </h3>
              <p className="text-[#A1A1AA] max-w-md">
                Proactive monitoring, security and support for your entire
                stack. We become the IT department you always wished you had.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="group relative overflow-hidden rounded-2xl border border-white/10 bg-surface min-h-[440px] flex flex-col justify-end p-8 md:p-10 hover:border-white/25 transition-colors duration-500">
            <img src={IMAGES.code} alt="Custom software development" className="absolute inset-0 h-full w-full object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/60 to-transparent" />
            <div className="relative">
              <Code2 className="h-9 w-9 text-accent mb-6" />
              <div className="font-mono text-xs tracking-[0.2em] text-[#71717A] mb-3">SERVICE 02</div>
              <h3 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mb-4">
                Custom Software Development
              </h3>
              <p className="text-[#A1A1AA] max-w-md">
                Web platforms, internal tools and integrations engineered around
                your workflow — not a template. Design, build, ship, iterate.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Supporting bento */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SUB.map((s, i) => (
            <Reveal
              key={s.title}
              delay={i * 0.08}
              className="group rounded-2xl border border-white/10 bg-surface p-8 hover:-translate-y-1 hover:border-white/25 transition-all duration-300"
            >
              <s.icon className="h-7 w-7 text-accent mb-6 group-hover:scale-110 transition-transform duration-300" />
              <h4 className="font-heading text-xl font-medium tracking-tight mb-2">{s.title}</h4>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
